import React from 'react';
import { useRedactionMode } from '../../contexts/RedactionModeContext';
import { RedactionBlur } from './RedactionBlur';
import { parsePiiMarkers, PiiSpan } from '../../utils/parsePiiMarkers';

interface RedactionTextProps {
  /** The text content to scan and potentially blur */
  text: string;
}

/**
 * Renders text with PII blurred. Applies two sources:
 * - Synthesis-time markers: {{PII:TYPE}}text{{/PII}} embedded by the LLM during generation
 * - Structured PII: instant string matching against known patient names, DOBs, etc.
 */
export const RedactionText: React.FC<RedactionTextProps> = ({ text }) => {
  const { isEnabled, structuredPii } = useRedactionMode();

  if (!text) return null;

  // Parse synthesis-time PII markers (always — strips markers even when redaction is off)
  const { cleanText, spans: markerSpans } = parsePiiMarkers(text);

  if (!isEnabled) {
    return <>{cleanText}</>;
  }

  const blurSpans = buildBlurSpans(cleanText, structuredPii, markerSpans);

  if (blurSpans.length === 0) {
    return <>{cleanText}</>;
  }

  return <>{renderWithBlurSpans(cleanText, blurSpans)}</>;
};

interface BlurSpan {
  start: number;
  end: number;
}

/**
 * Build a merged list of character ranges to blur from marker spans and structured PII.
 */
function buildBlurSpans(
  text: string,
  structuredPii: Set<string>,
  markerSpans: PiiSpan[]
): BlurSpan[] {
  const spans: BlurSpan[] = [];

  // Structured PII: find all occurrences of known PII strings (case-insensitive).
  // Use word-boundary checks to avoid matching inside unrelated words
  // (e.g. patient name "Ann" should not blur part of "nanny").
  const lowerText = text.toLowerCase();
  const isWordChar = (ch: string | undefined) => !!ch && /\w/.test(ch);

  for (const piiValue of structuredPii) {
    const lowerPii = piiValue.toLowerCase();
    let searchFrom = 0;
    while (searchFrom < lowerText.length) {
      const idx = lowerText.indexOf(lowerPii, searchFrom);
      if (idx === -1) break;
      const end = idx + piiValue.length;
      // Check word boundaries: character before start and after end
      // must not be a word character (letter/digit/underscore).
      const boundedStart = !isWordChar(lowerText[idx - 1]);
      const boundedEnd = !isWordChar(lowerText[end]);
      if (boundedStart && boundedEnd) {
        spans.push({ start: idx, end });
      }
      searchFrom = idx + 1;
    }
  }

  // Synthesis-time marker spans (offsets pre-computed, no drift)
  for (const span of markerSpans) {
    spans.push({ start: span.start, end: span.end });
  }

  return mergeSpans(spans);
}

function mergeSpans(spans: BlurSpan[]): BlurSpan[] {
  if (spans.length === 0) return [];

  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const merged: BlurSpan[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    if (sorted[i].start <= last.end) {
      last.end = Math.max(last.end, sorted[i].end);
    } else {
      merged.push(sorted[i]);
    }
  }

  return merged;
}

function renderWithBlurSpans(text: string, spans: BlurSpan[]): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (const span of spans) {
    if (cursor < span.start) {
      parts.push(text.substring(cursor, span.start));
    }

    parts.push(
      <RedactionBlur key={`pii-${span.start}`} forceBlur>
        {text.substring(span.start, span.end)}
      </RedactionBlur>
    );

    cursor = span.end;
  }

  if (cursor < text.length) {
    parts.push(text.substring(cursor));
  }

  return parts;
}
